import { Accordion, AccordionButton, AccordionItem, AccordionPanel } from "@chakra-ui/react";
import "./style.scss";
import Tabela from "./Tabela";
import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import InfiniteScroll from "react-infinite-scroll-component";
import { saveLocalStorage } from "~services/utils/SaveIndexBD";
import Loader from "~components/Form/components/Loader";
import { ClearMM } from "~misc/icons";
import { useTableState } from "~pages/PricesTableV2/context/TableStateContext";
import useNegociacaoPrecoRegra from "~/fetchs/useNegociacaoPrecoRegra";
import { useLiveQuery } from "dexie-react-hooks";
import { db } from "~/db/db";

const ContainerDoPrecoCliente = ({
  setShowFooterButtons,
  setCopyData,
  filters,
  handleChangeCheckbox,
  handleAddButton,
  handleDeleteButton,
  id,
  groupByClient = true,
  precosCliente,
  loadingSearch,
  take,
  setSkip,
  totalRenderizado,
  total,
  selectAll,
  setSelectAll,
  typeSelectAll,
  cardCliente,
  aplicaServicos,
  showCheckboxes = true,
  negotiationsDisabled = false,
}) => {
  const buttonRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState({});
  const [clearKey, setClearKey] = useState(0);
  const { clearAll } = useTableState();

  useNegociacaoPrecoRegra({ skip: 0, take: 0 });

  const pendingNegociacoes = useLiveQuery(
    () => db.negociacaoPrecoRegra.filter(n => n.statusNegociacaoLabel === "Pendente").toArray(),
    []
  ) ?? [];

  const produtosPendentesSet = useMemo(() => {
    const keys = pendingNegociacoes.flatMap(n =>
      (n.grupoProdutos || []).map(p => `${n.clienteId}-${p.filialId}-${p.produtoId}`)
    );
    return new Set(keys);
  }, [pendingNegociacoes]);

  const addEmAprovacao = (table, clienteIdFallback) => ({
    ...table,
    produtos: (table.produtos || []).map(p => ({
      ...p,
      emAprovacao: produtosPendentesSet.has(
        `${p.clienteId ?? clienteIdFallback}-${p.filialId}-${p.produtoId}`
      ),
    })),
  });

  const handleReload = () => {
    setLoading(true);

    clearAll();

    if (showCheckboxes) {
      if (setShowFooterButtons) setShowFooterButtons(false);
      setSelectedItems({});
      if (setSelectAll) setSelectAll(null);
    }
    setClearKey((prev) => prev + 1);

    setLoading(false);
  };

  useEffect(() => {
    if (typeof setCopyData == "function") setCopyData(precosCliente);
  }, [precosCliente, setCopyData]);

  useEffect(() => {
    const handleScroll = () => {
      if (buttonRef.current) {
        const margin = 10;
        if (window.scrollY >= buttonRef.current.offsetTop - margin) buttonRef.current.classList.add("sticky");
        else buttonRef.current.classList.remove("sticky");
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const handleCheckboxChange = (id) => {
    setSelectedItems((prevState) => ({
      ...prevState,
      [id]: !prevState[id],
    }));
  };

  const extractProdutosFromItem = (item) => {
    const tables = (item.vigencias ?? item.destaques ?? []).concat(item.precos ?? item.outros ?? []);
    return (tables ?? []).flatMap((table) => table.produtos ?? []);
  };

  const getAllSelectedItems = (precosCliente) => {
    const allSelected = {};
    (precosCliente ?? []).flatMap(extractProdutosFromItem).forEach((produto) => {
      if (produto && produto.id != null) allSelected[produto.id] = true;
    });
    return allSelected;
  };

  useEffect(() => {
    if (selectAll) {
      setSelectedItems(getAllSelectedItems(precosCliente));
    } else {
      if (selectAll === false) setSelectedItems({});
      if (typeSelectAll && setSelectAll) setSelectAll(null);
    }
  }, [selectAll, precosCliente, typeSelectAll, setSelectAll]);

  useEffect(() => {
    const values = Object.keys(selectedItems) || [];
    saveLocalStorage("tabela-preco-alteracoes-ids", values, true);
  }, [selectedItems]);

  const loadMoreItems = () => {
    setSkip((prevSkip) => prevSkip + take);
  };

  const checkContent = (item) => {
    const produtos = (precosCliente || []).flatMap((pc) => (pc.outros || []).flatMap((o) => o.produtos ?? []));

    return (
      <>
        {produtos?.some((tpc) => tpc.emAprovacao) && (
          <p className="font-bold my-4" style={{ color: "red" }}>
            Existem descontos ainda não aprovados para esse cliente no Portal de Aprovações
          </p>
        )}
        {(item.vigencias ?? item.destaques ?? []).map((table, index) => (
          <Tabela
            key={`${index}-${clearKey}`}
            data={addEmAprovacao(table, item.clienteId)}
            selectedItems={selectedItems}
            handleCheckboxChange={handleCheckboxChange}
            hideNegociacao={item.hideNegociacao}
            handleChangeCheckbox={handleChangeCheckbox}
            handleAddButton={handleAddButton}
            handleDeleteButton={handleDeleteButton}
            hidePrazo={false}
            cardClienteId={cardCliente}
            aplicaServicos={aplicaServicos}
            showCheckboxes={showCheckboxes}
            negotiationsDisabled={negotiationsDisabled}
          />
        ))}
        {(item.precos ?? item.outros ?? []).map((table, index) => (
          <Tabela
            key={`${index}-${clearKey}-outros`}
            data={addEmAprovacao(table, item.clienteId)}
            selectedItems={selectedItems}
            handleCheckboxChange={handleCheckboxChange}
            hideNegociacao={false}
            handleChangeCheckbox={handleChangeCheckbox}
            handleAddButton={handleAddButton}
            handleDeleteButton={handleDeleteButton}
            hidePrazo={false}
            id={id}
            cardClienteId={cardCliente}
            aplicaServicos={aplicaServicos}
            showCheckboxes={showCheckboxes}
            negotiationsDisabled={negotiationsDisabled}
          />
        ))}
      </>
    );
  };

  return (
    <div>
      <button
        type="button"
        className="fixed bottom-[9rem] right-[2rem] bg-primaria text-white p-4 rounded-full z-50 shadow-lg hover:bg-[#9ACA3C]"
        onClick={handleReload}
      >
        <ClearMM />{" "}
      </button>
      {loading ? (
        <Loader />
      ) : (
        <InfiniteScroll
          dataLength={totalRenderizado || 0}
          next={loadMoreItems}
          hasMore={total > totalRenderizado}
          loader={loading && <Loader />}
          endMessage={
            precosCliente && (precosCliente || []).length > 0 ? <p className="text-center">Fim da lista</p> : null
          }
        >
          <main className="prices-table-table">
            <div className="container content">
              {(precosCliente || []).map((item) => (
                <>
                  {groupByClient ? (
                    <Accordion
                      className="accordion"
                      data-test-id="accordion-precos-cliente"
                      allowToggle
                      defaultIndex={[0]}
                    >
                      <AccordionItem>
                        <AccordionButton ref={buttonRef}>{item.labelHeader}</AccordionButton>
                        <AccordionPanel>{checkContent(item)}</AccordionPanel>
                      </AccordionItem>
                    </Accordion>
                  ) : (
                    checkContent(item)
                  )}
                </>
              ))}
              {loadingSearch && <Loader />}
              {precosCliente &&
                (precosCliente || []).length === 0 &&
                (filters?.grupoProduto !== 0 || filters?.produto !== 0 || filters?.filial !== 0 ? (
                  <p className="text-center">Nenhum resultado encontrado</p>
                ) : (
                  <p className="text-center">Não há informações cadastradas para esses filtros</p>
                ))}
            </div>
          </main>
        </InfiniteScroll>
      )}
    </div>
  );
};

ContainerDoPrecoCliente.propTypes = {
  setShowFooterButtons: PropTypes.func,
  setCopyData: PropTypes.func,
  filters: PropTypes.object,
  handleChangeCheckbox: PropTypes.func,
  handleAddButton: PropTypes.func,
  handleDeleteButton: PropTypes.func,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  groupByClient: PropTypes.bool,
  precosCliente: PropTypes.array,
  loadingSearch: PropTypes.bool,
  take: PropTypes.number,
  setSkip: PropTypes.func,
  totalRenderizado: PropTypes.number,
  total: PropTypes.number,
  selectAll: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  setSelectAll: PropTypes.func,
  typeSelectAll: PropTypes.any,
  cardCliente: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  aplicaServicos: PropTypes.bool,
  showCheckboxes: PropTypes.bool,
  negotiationsDisabled: PropTypes.bool,
};

export default ContainerDoPrecoCliente;
